/**
 * MLB categories read API: search categories and list children (for linking product categories).
 * Default and max limits must match backend (api/mlb_categories, categories_service).
 */
import axios from 'axios';
import { authHeaders } from './apiAuth';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

/** Default page size for search and children; matches backend DEFAULT_LIMIT. */
export const MLB_CATEGORIES_DEFAULT_LIMIT = 20;
/** Max allowed by backend; matches backend MAX_LIMIT. */
export const MLB_CATEGORIES_MAX_LIMIT = 50;

/**
 * Search MLB categories by name (parent categories).
 * @param {string} accessToken - User's access token
 * @param {string} [q] - Search query (category name filter)
 * @param {number} [limit=20] - Max results (capped at 50 by API)
 * @returns {Promise<Object>} { success, categories } where categories have category_id, category_name, catalog_domain
 */
function capLimit(limit) {
  if (limit == null) return MLB_CATEGORIES_DEFAULT_LIMIT;
  const n = Math.floor(Number(limit));
  return Math.min(Math.max(1, n), MLB_CATEGORIES_MAX_LIMIT);
}

export const searchMlbCategories = async (accessToken, q, limit = MLB_CATEGORIES_DEFAULT_LIMIT) => {
  try {
    const params = {};
    if (q != null && String(q).trim() !== '') params.q = String(q).trim();
    params.limit = capLimit(limit);
    const response = await axios.get(`${API_BASE_URL}/api/mlb-categories`, {
      params,
      headers: authHeaders(accessToken)
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to search MLB categories');
  }
};

/**
 * Get child categories for an MLB parent.
 * @param {string} accessToken - User's access token
 * @param {string} parentId - MLB parent category_id
 * @param {string} [q] - Optional filter by child name
 * @param {number} [limit=20] - Max results (capped at 50 by API)
 * @returns {Promise<Object>} { success, children } where children have child_id, child_name
 */
export const getMlbCategoryChildren = async (accessToken, parentId, q, limit = MLB_CATEGORIES_DEFAULT_LIMIT) => {
  try {
    const params = {};
    if (q != null && String(q).trim() !== '') params.q = String(q).trim();
    params.limit = capLimit(limit);
    const response = await axios.get(
      `${API_BASE_URL}/api/mlb-categories/${encodeURIComponent(parentId)}/children`,
      { params, headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get MLB category children');
  }
};

/**
 * Get attributes and their allowed values for an MLB category (for product additional details).
 * @param {string} accessToken - User's access token
 * @param {string} mlbCategoryId - MLB category id (child or parent)
 * @returns {Promise<Object>} { success, attributes } where each attribute has attribute_id, name, values: [{ value_id, value_name, value_kind }]
 */
export const getMlbCategoryAttributes = async (accessToken, mlbCategoryId) => {
  try {
    const response = await axios.get(
      `${API_BASE_URL}/api/mlb-categories/${encodeURIComponent(mlbCategoryId)}/attributes`,
      { headers: authHeaders(accessToken) }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to get MLB category attributes');
  }
};
